import { notificationServiceInstance } from '@/services/notification.service'
import { userServiceInstance } from '@/services/user.service'
import { userAtom } from '@/store/atoms/user.atom'
import { useSetAtom } from 'jotai'
import { createContext, useCallback, useEffect, useRef, type ReactNode } from 'react'
import toast from 'react-hot-toast'

const POLL_INTERVAL_MS = 10000

export const NotificationContext = createContext({})

type Props = {
  children: ReactNode
}

export function NotificationContextProvider(props: Props) {
  const { children } = props

  const setUser = useSetAtom(userAtom)
  const notifiedIds = useRef<Set<number>>(new Set())

  // Sem WebSocket: toda notificação corresponde a uma mudança de saldo
  // (ex.: estorno de saque que falhou). Rebusca o saldo via REST. Ver docs/withdraw.md §5.
  const refetchWalletBalance = useCallback(async () => {
    try {
      const amount = await userServiceInstance.wallet()
      setUser(prev => (prev ? { ...prev, wallet: { ...prev.wallet, amount } } : prev))
    } catch (e) {
      console.error(e)
    }
  }, [setUser])

  const checkNotifications = useCallback(async () => {
    const notification = await notificationServiceInstance.me()

    if (!notification) return
    if (notification.read !== 0) return
    if (notifiedIds.current.has(notification.id)) return

    notifiedIds.current.add(notification.id)
    toast.error(notification.message)
    refetchWalletBalance()
  }, [refetchWalletBalance])

  useEffect(() => {
    checkNotifications()
    const interval = setInterval(checkNotifications, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [checkNotifications])

  return (
    <NotificationContext.Provider value={{}}>
      {children}
    </NotificationContext.Provider>
  )
}
